import type { FC } from "react";
import { useMemo, useState } from "react";
import { Autocomplete, TextField, CircularProgress, useTheme } from "@mui/material";
import { FoodsSelectServerPagination } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { useOrganizationSelector } from "src/api/client-api/company/useOrganizationSelector";
import { useSupplierParentOrganizations } from "src/api/client-api/company/useSupplierParentOrganizations";
import { FormFieldLoadError } from "src/components/EntityForm/FormFieldLoadError";
import { FoodsSelectTriggerAdapter } from "src/components/EntityForm/FoodsSelectTriggerAdapter";

export interface CompanyOption {
    "@id": string;
    id: number;
    name: string;
    [key: string]: unknown;
}

interface CompanyAutocompleteProps {
    value: CompanyOption[];
    onChange: (options: CompanyOption[]) => void;
    placeholder?: string;
    disabled?: boolean;
    excludeIds?: number[];
    hierarchyRootId?: number;
    appearance?: "legacy" | "product-v2";
    accessibleLabel?: string;
    labelId?: string;
    source?: "organizations" | "supplier-parents";
}

export const CompanyAutocomplete: FC<CompanyAutocompleteProps> = ({
    value,
    onChange,
    placeholder,
    hierarchyRootId,
    appearance = "legacy",
    accessibleLabel,
    labelId,
    source = "organizations",
    disabled = false,
    excludeIds = [],
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { search, delayedSearch, setSearch, doesSearchMatch } = useDelayedSearch();
    const [inputValue, setInputValue] = useState("");

    // Use server-side search with debounced search term
    const isSupplierParentSource = source === "supplier-parents";
    const organizationChoices = useOrganizationSelector({
        searchText: delayedSearch,
        enabled: !isSupplierParentSource,
        itemsPerPage: 50,
        hierarchyRootId,
    });
    const supplierParentChoices = useSupplierParentOrganizations({
        searchText: delayedSearch,
        enabled: isSupplierParentSource,
        itemsPerPage: 50,
    });
    const {
        companies = [],
        error,
        isLoading,
        refetch,
    } = isSupplierParentSource ? supplierParentChoices : organizationChoices;

    // Filter out excluded IDs and ensure current values are always available
    const filteredCompanies = useMemo(() => {
        let filtered = companies.filter((company) => !excludeIds.includes(company.id));

        // Always include current values if they exist and aren't already in the list
        if (value && value.length > 0) {
            value.forEach((val) => {
                if (!filtered.some((company) => company["@id"] === val["@id"])) {
                    filtered = [val, ...filtered];
                }
            });
        }

        return filtered;
    }, [companies, excludeIds, value]);

    const productV2LoadError = appearance === "product-v2" && error !== null && error !== undefined;
    const hasUsableProductV2Options = filteredCompanies.length > 0 || value.length > 0;

    if (productV2LoadError && !hasUsableProductV2Options) {
        return (
            <FormFieldLoadError
                message={t("company.select.loadError", "Organizations could not be loaded.")}
                retryLabel={t("common.retry", "Retry")}
                onRetry={refetch}
            />
        );
    }

    if (appearance === "product-v2") {
        return (
            <>
                {productV2LoadError && (
                    <FormFieldLoadError
                        message={t("company.select.loadError", "Organizations could not be loaded.")}
                        retryLabel={t("common.retry", "Retry")}
                        onRetry={refetch}
                    />
                )}
                <FoodsSelectTriggerAdapter accessibleLabel={accessibleLabel} labelId={labelId}>
                    <FoodsSelectServerPagination
                        selectedOption={null}
                        options={filteredCompanies}
                        onChange={(company) => {
                            if (company !== null) {
                                onChange([...value, company]);
                            }
                        }}
                        getId={(company) => company.id}
                        getName={(company) => company.name}
                        textOptions={{
                            placeholder: placeholder || t("company.select.placeholder", "Select parent companies"),
                            searchPlaceholder: t("company.select.searchPlaceholder", "Search companies..."),
                            emptyOptionsText: t("company.select.noOptions", "No companies available"),
                            noResult:
                                isLoading ?
                                    t("company.select.loading", "Loading...")
                                :   t("company.select.noResultFound", "No companies found"),
                        }}
                        search={search}
                        setSearch={setSearch}
                        isSearchFound={doesSearchMatch}
                        disabled={disabled}
                        closeOnSelect
                        BoxProps={{ width: "100%" }}
                    />
                </FoodsSelectTriggerAdapter>
            </>
        );
    }

    return (
        <Autocomplete
            multiple
            options={filteredCompanies}
            value={value}
            inputValue={inputValue}
            onChange={(_, newValue) => {
                onChange(newValue);
                setInputValue("");
            }}
            onInputChange={(_, newInputValue, reason) => {
                // MUI emits "reset" whenever the `value` reference changes, and callers pass a fresh array per render.
                if (reason === "reset") return;
                setInputValue(newInputValue);
                setSearch(newInputValue);
            }}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option["@id"] === value["@id"]}
            filterSelectedOptions
            loading={isLoading}
            disabled={disabled}
            filterOptions={(options) => options}
            noOptionsText={
                isLoading ? t("common.loading", "Loading...")
                : delayedSearch ?
                    t("common.noResults", "No results")
                :   t("common.typeToSearch", "Type to search...")
            }
            loadingText={t("common.loading", "Loading...")}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={placeholder || t("company.select.placeholder", "Select parent companies")}
                    variant="outlined"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {isLoading && delayedSearch ?
                                    <CircularProgress color="inherit" size={theme.spacing(2.5)} />
                                :   null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                    sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-root": {
                            backgroundColor: theme.custom.grey[100],
                            borderColor: theme.custom.grey[500],
                            borderRadius: theme.spacing(3),
                            borderStyle: theme.custom.strokes.containerDivider.style,
                            borderWidth: theme.custom.strokes.containerDivider.width,
                            boxSizing: "border-box",
                            "& fieldset": {
                                border: "none",
                            },
                            "& input": {
                                ...theme.typography["body-medium"],
                                color: theme.custom.grey[2800],
                                padding: theme.spacing(0, 1.5, 0, 2.5),
                                "&::placeholder": {
                                    color: theme.custom.grey[1400],
                                    opacity: 1,
                                },
                            },
                        },
                    }}
                />
            )}
        />
    );
};
